import { Box, Center, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { IconBuildingFactory2, IconRosetteDiscountCheckFilled } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import { getSubIndustryLabel } from "@/components/shared/constants/general.const";
import {
  buildCompanyDiscoverPath,
  buildLoggedInCompanyProfilePath,
} from "@/shared/utils/companyProfileLink.util";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import type { SectorEcosystemOverview } from "../../../../../../shared/sectorEcosystemOverview";
import type { SectorListFooterLink } from "../sectorCompaniesList.const";
import { useSectorCompaniesPreview } from "../hooks/useSectorCompaniesPreview";
import SectorDashboardCard from "./SectorDashboardCard";

type CompaniesOverviewProps = {
  slug: string;
  overview: SectorEcosystemOverview;
  isAuthenticated: boolean;
} & SectorListFooterLink;

const GRID_TEMPLATE = "minmax(0, 0.9fr) minmax(0, 1.6fr) minmax(0, 0.9fr)";

function FeaturedCompaniesComingSoonContent() {
  return (
    <Paper
      p="md"
      radius="md"
      style={{
        border: "1px dashed #CBD5E1",
        backgroundColor: "#F8FAFC",
      }}
    >
      <Stack gap="sm" align="center" py="sm">
        <Center
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: "#EFF6FF",
          }}
        >
          <IconBuildingFactory2 size={20} color="#2563EB" />
        </Center>
        <Text
          size="xs"
          fw={600}
          c="#2563EB"
          px={10}
          py={4}
          style={{ borderRadius: 999, backgroundColor: "#EFF6FF" }}
        >
          Launching Soon
        </Text>
        <Stack gap={4} align="center" maw={300}>
          <Text size="sm" fw={600} c="#334155" ta="center">
            Companies are in the onboarding queue
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            Featured companies for this sector are being onboarded and will appear here once live.
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
}

function CompanyPreviewHeader() {
  return (
    <Box
      px="sm"
      py={8}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_TEMPLATE,
        gap: 12,
        backgroundColor: "#F8FAFC",
        borderRadius: 6,
      }}
    >
      <Text size="xs" fw={600} c="#64748B" tt="uppercase">
        GTID
      </Text>
      <Text size="xs" fw={600} c="#64748B" tt="uppercase">
       Industry
      </Text>
      <Text size="xs" fw={600} c="#64748B" tt="uppercase">
        Country
      </Text>
    </Box>
  );
}

export default function CompaniesOverview({
  slug,
  overview,
  isAuthenticated,
  footerTo,
  footerLinkState,
}: CompaniesOverviewProps) {
  const location = useLocation();
  const sectorReturnTo = `${location.pathname}${location.search}${location.hash}`;
  const { companies, loading, error } = useSectorCompaniesPreview(slug);

  const companiesListReady = !loading && companies.length > 0;

  return (
    <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
    <SectorDashboardCard
      title="Featured Companies"
      footerLabel="View All Companies"
      footerComingSoon={!companiesListReady}
      footerTo={companiesListReady ? footerTo : undefined}
      footerLinkState={companiesListReady ? footerLinkState : undefined}
    >
      {loading ? (
        <Group justify="center" py="lg">
          <Loader size="sm" />
        </Group>
      ) : error ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          {error}
        </Text>
      ) : companies.length === 0 ? (
        <FeaturedCompaniesComingSoonContent />
      ) : (
        <Stack gap={0}>
          <CompanyPreviewHeader />
          {companies.map((company, index) => {
            const subIndustryKey = company.subIndustry?.[0];
            const subIndustryLabel = subIndustryKey ? getSubIndustryLabel(subIndustryKey) : "—";
            const country = company.headquartersCountry?.trim() || "—";
            const flag = flagEmojiFromHeadquartersCountry(company.headquartersCountry);
            const profilePath = isAuthenticated
              ? buildLoggedInCompanyProfilePath(company.id)
              : buildCompanyDiscoverPath(company.id);

            return (
              <Box
                key={company.id}
                component={Link}
                to={profilePath}
                state={{ discoverReturnTo: sectorReturnTo }}
                px="sm"
                py={10}
                style={{
                  display: "grid",
                  gridTemplateColumns: GRID_TEMPLATE,
                  gap: 12,
                  alignItems: "start",
                  textDecoration: "none",
                  color: "inherit",
                  borderTop: index === 0 ? "1px solid #E2E8F0" : undefined,
                  borderBottom: "1px solid #E2E8F0",
                  transition: "background-color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <Group gap={6} wrap="nowrap">
                  <Text size="sm" fw={600} c="#04305c" truncate>
                    {company.gtid || "—"}
                  </Text>
                  {company.status === "VERIFIED" ? (
                    <IconRosetteDiscountCheckFilled
                      size={14}
                      style={{ color: "var(--mantine-color-primary-6)", flexShrink: 0 }}
                    />
                  ) : null}
                </Group>
                <Text
                  size="sm"
                  c={subIndustryKey ? "#334155" : "dimmed"}
                  lh={1.45}
                  style={{ wordBreak: "break-word", whiteSpace: "normal" }}
                >
                  {subIndustryLabel}
                </Text>
                <Stack gap={4} align="flex-start">
                  {flag ? (
                    <Box component="span" style={{ flexShrink: 0, lineHeight: 0 }}>
                      {flag}
                    </Box>
                  ) : null}
                  <Text
                    size="sm"
                    c="#334155"
                    lh={1.45}
                    style={{ wordBreak: "break-word", whiteSpace: "normal" }}
                  >
                    {country}
                  </Text>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </SectorDashboardCard>
    </Box>
  );
}
